/* ==========================================================================
   Dashboard metric metadata — the single source of truth for every KPI and
   chart's STATIC presentation: title, description, "how it's calculated"
   formula, chart type, layout (group / span / height) and which secondary
   sub-metrics a KPI card shows.

   The backend (services/dashboard_analytics_service.py) now returns only the
   dynamic numbers:

       { "kpis": { id: value }, "charts": { id: { labels, datasets } } }

   dashboardChartAdapter.buildDashboard() merges this file with that payload.

   Keys:
     - one entry per dashboard tab id (matches TAB_DEFS on the backend)
     - the AI tab is split into `ai_analytics` and `ai_coding` (the ?ai_type
       engine toggle) — buildDashboard is called with `ai_${aiType}`.
       Source: the per-response entries the backend unnests from the audit-log
       "AiSession" rows (one row per conversation, metrics per chat_id).

   A KPI card renders only when its `key` is present in payload.kpis, so tabs
   whose KPI set differs by perspective (superadmin vs admin) can list every
   possible card here and the right four show up.
   ========================================================================== */

/* --- shared chart layout: the classic 6-chart dashboard rhythm ----------- */
const L = {
  trendA: { group: "Trends & momentum", span: 6, height: 360 },
  trendB: { group: "Trends & momentum", span: 6, height: 360 },
  compA: { group: "Composition", span: 4, height: 372 },
  compB: { group: "Composition", span: 4, height: 372 },
  compC: { group: "Composition", span: 4, height: 372 },
  signal: { group: "Signal", span: 12, height: 440 },
};

const CALC_FALLBACK =
  "Aggregated in the database from live records, then cached for a few minutes.";

const OV = {
  momentum: { group: "Momentum", span: 6, height: 340 },
  composition: { group: "Composition & status", span: 6, height: 360 },
};

export const TAB_METRICS = {
  dashboard: {
    kpis: [
      {
        key: "kpi_ov_clients", title: "Total Clients", tone: "teal", icon: "clients",
        description: "Client organizations you can see, excluding deleted ones.",
        formula: "COUNT(clients) where status <> Deleted.",
        secondary: [
          { key: "kpi_ov_clients_active", label: "Active" },
          { key: "kpi_ov_clients_inactive", label: "Inactive" },
        ],
      },
      {
        key: "kpi_ov_providers", title: "Total Providers", tone: "blue", icon: "providers",
        description: "Registered providers you can see, any status.",
        formula: "COUNT(providers) in scope.",
        secondary: [
          { key: "kpi_ov_providers_active", label: "Active" },
          { key: "kpi_ov_providers_inactive", label: "Inactive" },
        ],
      },
      {
        key: "kpi_ov_patients", title: "Total Patients", tone: "violet", icon: "patients",
        description: "Patient records you can see.",
        formula: "COUNT(patients) in scope.",
        secondary: [
          { key: "kpi_ov_patients_insured", label: "Insured" },
          { key: "kpi_ov_patients_uninsured", label: "Uninsured" },
        ],
      },
      {
        key: "kpi_ov_locations", title: "Total Locations", tone: "green", icon: "locations",
        description: "Clinic locations you can see, any status.",
        formula: "COUNT(locations) in scope.",
        secondary: [
          { key: "kpi_ov_locations_active", label: "Active" },
          { key: "kpi_ov_locations_inactive", label: "Inactive" },
        ],
      },
      {
        key: "kpi_ov_credentials", title: "Login Credentials", tone: "teal", icon: "login_credentials",
        description: "Portal / payer login records you can see.",
        formula: "COUNT(login_credentials) in scope.",
        secondary: [
          { key: "kpi_ov_credentials_active", label: "Active" },
          { key: "kpi_ov_credentials_inactive", label: "Inactive" },
        ],
      },
      {
        key: "kpi_ov_credentialing", title: "Payer Applications", tone: "blue", icon: "credentialing",
        description: "Provider-to-payer enrolment records you can see.",
        formula: "COUNT(healthcare_payors) joined to providers in scope.",
        secondary: [
          { key: "kpi_ov_credentialing_approved", label: "Approved" },
          { key: "kpi_ov_credentialing_pending", label: "Pending" },
        ],
      },
      {
        key: "kpi_ov_users", title: "Total Users", tone: "violet", icon: "users",
        description: "User accounts you can see, any status.",
        formula: "COUNT(users) in scope.",
        secondary: [
          { key: "kpi_ov_users_active", label: "Active" },
          { key: "kpi_ov_users_inactive", label: "Inactive" },
        ],
      },
      {
        key: "kpi_ov_roles", title: "Total Roles", tone: "green", icon: "roles_permissions",
        description: "Roles defined in scope, any status.",
        formula: "COUNT(roles) in scope.",
        secondary: [
          { key: "kpi_ov_roles_active", label: "Active" },
          { key: "kpi_ov_roles_inactive", label: "Inactive" },
        ],
      },
      {
        key: "kpi_ov_subscriptions", title: "Active Subscriptions", tone: "teal", icon: "subscription",
        description: "Subscriptions you can see currently in active billing.",
        formula: "COUNT(subscribers) where status = Active.",
        secondary: [
          { key: "kpi_ov_subs_mrr", label: "MRR", unit: "$" },
          { key: "kpi_ov_subs_inactive", label: "Inactive" },
        ],
      },
      {
        key: "kpi_ov_audit", title: "Audit Events This Month", tone: "blue", icon: "audit_logs",
        description: "Audit log entries recorded since the 1st of this month.",
        formula: "COUNT(audit_logs) where created_on >= first day of the current month.",
        secondary: [
          { key: "kpi_ov_audit_today", label: "Today" },
          { key: "kpi_ov_audit_actors", label: "Actors" },
        ],
      },
      {
        key: "kpi_ov_ai", title: "AI Interactions", tone: "violet", icon: "ai",
        description: "Analytics AI and Coding AI responses recorded for the engines you can see.",
        formula: "COUNT of AI responses across your visible engines.",
        secondary: [
          { key: "kpi_ov_ai_success", label: "Success" },
          { key: "kpi_ov_ai_errors", label: "Errors" },
        ],
      },
    ],
    charts: [
      { id: "ov_clients_growth", type: "area", title: "Cumulative Client Growth", ...OV.momentum,
        description: "Running total of client accounts over the last 6 months.",
        formula: "Clients created before the window, plus a running sum per month." },
      { id: "ov_users_trend", type: "line", title: "User Registration Trend", ...OV.momentum,
        description: "New user accounts created each month (last 6 months).",
        formula: "COUNT(users) grouped by month of created_on." },
      { id: "ov_patients_growth", type: "combo", title: "Patient Growth", ...OV.momentum,
        description: "New patients added each month and the running total.",
        formula: "Bars: COUNT(patients) grouped by month of created_on. Line: the cumulative total." },
      { id: "ov_subscription_revenue", type: "combo", title: "Revenue Collected", ...OV.momentum,
        description: "Subscription payments received each month and the running total.",
        formula: "Bars: SUM(transaction_amount) of PAID transactions grouped by month. Line: the cumulative sum." },
      { id: "ov_audit_volume", type: "area", title: "Monthly Audit Volume", ...OV.momentum,
        description: "Audit entries per month (last 12 months).",
        formula: "COUNT(audit_logs) grouped by month of created_on." },
      { id: "ov_ai_tokens", type: "line", title: "AI Tokens per Day", ...OV.momentum,
        description: "AI token consumption per day over the last 30 days.",
        formula: "SUM(total_tokens) grouped by day across the engines you can see." },

      { id: "ov_providers_expiry", type: "bar", title: "Licence Expiry Windows", ...OV.composition,
        description: "How soon current provider licences expire.",
        formula: "COUNT(providers) bucketed by days until license_exp_date (0-30 / 31-60 / 61-90 / >90)." },
      { id: "ov_locations_status", type: "doughnut", title: "Location Status", ...OV.composition,
        description: "Locations by status.",
        formula: "COUNT(locations) grouped by status." },
      { id: "ov_credentials_status", type: "doughnut", title: "Credential Status", ...OV.composition,
        description: "Login records by status.",
        formula: "COUNT(login_credentials) grouped by status." },
      { id: "ov_credentialing_progress", type: "bar", title: "Application Progress", ...OV.composition,
        description: "Where payer applications sit in the pipeline.",
        formula: "COUNT(healthcare_payors) bucketed: not started / submitted / resubmitted / approved." },
      { id: "ov_roles_users", type: "bar_horizontal", title: "Users per Role", ...OV.composition,
        description: "Headcount assigned to each role (top 10).",
        formula: "COUNT(users) grouped by role, top 10." },
    ],
  },

  /* ===================================================== CLIENTS ======== */
  clients: {
    kpis: [
      { key: "kpi_total_clients", title: "Total Clients", tone: "teal",
        description: "Client accounts in scope, excluding deleted ones.",
        formula: "COUNT(clients) where status <> Deleted." },
      { key: "kpi_active_clients", title: "Active Clients", tone: "blue",
        description: "Client accounts currently in active status.",
        formula: "COUNT(clients) where status = Active." },
      { key: "kpi_new_clients", title: "New This Month", tone: "violet",
        description: "Client accounts created since the 1st of this month.",
        formula: "COUNT(clients) where created_on >= first day of the current month." },
      { key: "kpi_client_orgs", title: "Total Organizations", tone: "green",
        description: "Clinic organizations belonging to these client accounts.",
        formula: "COUNT(organizations) in scope." },
    ],
    charts: [
      { id: "cl_trend", type: "line", title: "Client Onboarding Trend", ...L.trendA,
        description: "New client accounts created each month (last 6 months).",
        formula: "COUNT(clients) grouped by month of created_on." },
      { id: "cl_cumulative", type: "area", title: "Cumulative Client Growth", ...L.trendB,
        description: "Running total of client accounts over time.",
        formula: "Clients created before the window, plus a running sum per month." },
      { id: "cl_orgs_per_client", type: "bar", title: "Organizations per Client", ...L.compA,
        description: "Clinic organizations registered under each client (top 10).",
        formula: "COUNT(organizations) grouped by client, top 10." },
      { id: "cl_users_per_client", type: "bar_horizontal", title: "Users per Client", ...L.compB,
        description: "User accounts belonging to each client (top 10).",
        formula: "COUNT(users) grouped by client, top 10." },
      { id: "cl_status", type: "doughnut", title: "Client Status Distribution", ...L.compC,
        description: "Client accounts by status.",
        formula: "COUNT(clients) grouped by status." },
      { id: "cl_footprint", type: "bar_grouped", title: "Client Footprint", ...L.signal,
        description: "Organizations and users side by side for the 10 largest clients.",
        formula: "Per client: COUNT(DISTINCT organizations) and COUNT(DISTINCT users), top 10 by organization count." },
    ],
  },

  /* ===================================================== PROVIDERS ===== */
  providers: {
    kpis: [
      { key: "kpi_prov_total", title: "Total Providers", tone: "teal",
        description: "Registered providers in scope, any status.",
        formula: "COUNT(providers) in scope.",
        secondary: [
          { key: "kpi_prov_status_active", label: "Active" },
          { key: "kpi_prov_status_inactive", label: "Inactive" },
        ] },
      { key: "kpi_prov_cred", title: "In Credentialing", tone: "blue",
        description: "Providers with the credentialing workflow flag switched on.",
        formula: "COUNT(providers) where is_credentialing = true." },
      { key: "kpi_prov_caqh", title: "CAQH Enabled", tone: "violet",
        description: "Providers with CAQH portal access enabled.",
        formula: "COUNT(providers) where caqh_enabled = true." },
      { key: "kpi_prov_exp90", title: "License Expiring", tone: "green",
        description: "Providers whose license expiry needs attention.",
        formula: "COUNT(providers) where license_exp_date is within the next 90 days.",
        secondary: [
          { key: "kpi_prov_exp90", label: "≤ 90 days" },
          { key: "kpi_prov_expired", label: "Expired" },
        ] },
    ],
    charts: [
      { id: "pr_reg_trend", type: "line", title: "Provider Registration Trend", ...L.trendA,
        description: "New providers onboarded each month (last 6 months).",
        formula: "COUNT(providers) grouped by month of created_on." },
      { id: "pr_licence_issued", type: "area", title: "Licenses Issued", ...L.trendB,
        description: "Provider license issue dates by month (last 12 months).",
        formula: "COUNT(providers) grouped by month of license_issue_date." },
      { id: "pr_licence_expiry", type: "bar", title: "License Expiry Windows", ...L.compA,
        description: "How soon current provider licenses expire.",
        formula: "COUNT(providers) bucketed by days until license_exp_date (0-30 / 31-60 / 61-90 / >90)." },
      { id: "pr_per_org", type: "bar_horizontal", title: "Providers per Organization", ...L.compB,
        description: "Provider headcount by organization (top 10).",
        formula: "COUNT(providers) grouped by organization, top 10." },
      { id: "pr_status", type: "doughnut", title: "Provider Status", ...L.compC,
        description: "The ten most common provider statuses.",
        formula: "COUNT(providers) grouped by status, top 10 by count." },
      { id: "pr_readiness", type: "bar_grouped", title: "Credential Readiness", ...L.signal,
        description:
          "Share of in-scope providers that satisfy each core credentialing requirement — " +
          "license on file & not expiring, CAQH enabled, active credentialing workflow, " +
          "NPI enumeration date recorded.",
        formula:
          "Per requirement: providers meeting it ÷ total providers, as a percentage; " +
          "the remainder are missing or incomplete." },
    ],
  },

  /* ===================================================== PATIENTS ====== */
  patients: {
    kpis: [
      { key: "kpi_pat_total", title: "Total Patients", tone: "teal",
        description: "Patient records in scope.",
        formula: "COUNT(patients) in scope." },
      { key: "kpi_pat_appt", title: "Total Appointments", tone: "blue",
        description: "Appointments booked for these patients (all statuses).",
        formula: "COUNT(appointments) joined to patients in scope." },
      { key: "kpi_pat_visits", title: "Total Visits", tone: "violet",
        description: "Documented visits recorded for these patients.",
        formula: "COUNT(visits) joined through appointments to patients in scope." },
      { key: "kpi_pat_insured", title: "Insured Patients", tone: "green",
        description: "Patients with a non-blank primary insurance on file.",
        formula: "COUNT(patients) where primary_insurance is set and not empty." },
    ],
    charts: [
      { id: "pt_appt_trend", type: "line", title: "Appointments per Month", ...L.trendA,
        description: "Appointment volume over the last 6 months (excludes deleted).",
        formula: "COUNT(appointments) where status <> Deleted, grouped by service month." },
      { id: "pt_visit_trend", type: "area", title: "Visits per Month", ...L.trendB,
        description: "Documented visit volume over the last 6 months.",
        formula: "COUNT(visits) grouped by the service month of their appointment." },
      { id: "pt_age_bands", type: "bar", title: "Patient Age Bands", ...L.compA,
        description: "Patients grouped into clinical age brackets.",
        formula: "COUNT(patients) with a known age, bucketed 0-17 / 18-34 / 35-49 / 50-64 / 65+." },
      { id: "pt_top_cpt", type: "bar_horizontal", title: "Top CPT Codes", ...L.compB,
        description: "The procedure codes most often recorded for these patients (top 10).",
        formula: "COUNT(visits) grouped by cpt_code (blank codes excluded), top 10." },
      { id: "pt_outcomes", type: "doughnut", title: "Appointment Outcomes", ...L.compC,
        description: "What happened to appointments in the last 6 months — seen, no-show, cancelled, still scheduled.",
        formula: "COUNT(appointments) where status <> Deleted, grouped by status, last 6 months." },
      { id: "pt_growth", type: "combo", title: "Patient Growth", ...L.signal,
        description: "New patients added each month and the running total.",
        formula: "Bars: COUNT(patients) grouped by month of created_on. Line: the cumulative total." },
    ],
  },

  /* ===================================================== LOCATIONS ==== */
  locations: {
    kpis: [
      { key: "kpi_loc_total", title: "Total Locations", tone: "teal",
        description: "Clinic locations in scope, any status.",
        formula: "COUNT(locations) in scope." },
      { key: "kpi_loc_active", title: "Active Locations", tone: "blue",
        description: "Locations currently in active status.",
        formula: "COUNT(locations) where status = Active." },
      { key: "kpi_loc_orgs", title: "Orgs with Locations", tone: "violet",
        description: "Organizations that have at least one location on file.",
        formula: "COUNT(DISTINCT organization_id) across locations in scope." },
      { key: "kpi_loc_new", title: "New This Month", tone: "green",
        description: "Locations added since the 1st of this month.",
        formula: "COUNT(locations) where created_on >= first day of the current month." },
    ],
    charts: [
      { id: "lo_trend", type: "line", title: "Location Registrations", ...L.trendA,
        description: "New locations added each month (last 6 months).",
        formula: "COUNT(locations) grouped by month of created_on." },
      { id: "lo_cumulative", type: "area", title: "Cumulative Locations", ...L.trendB,
        description: "Running total of locations over time (last 12 months).",
        formula: "Locations created before the window, plus a running sum per month." },
      { id: "lo_per_org", type: "bar", title: "Locations per Organization", ...L.compA,
        description: "Locations by organization (top 10).",
        formula: "COUNT(locations) grouped by organization, top 10." },
      { id: "lo_per_client", type: "bar_horizontal", title: "Locations per Client", ...L.compB,
        description: "Locations by client account (top 10).",
        formula: "COUNT(locations) grouped by client, top 10." },
      { id: "lo_status", type: "doughnut", title: "Location Status", ...L.compC,
        description: "Locations by status.",
        formula: "COUNT(locations) grouped by status." },
      { id: "lo_coverage", type: "bar_grouped", title: "Location Coverage by Client", ...L.signal,
        description: "For each client, how many of its organizations actually have a location (top 10 clients by organization count).",
        formula: "Per client: COUNT(DISTINCT organizations) vs COUNT(DISTINCT organizations that appear in the locations table)." },
    ],
  },

  /* =============================================== LOGIN CREDENTIALS == */
  login_credentials: {
    kpis: [
      { key: "kpi_cred_total", title: "Total Credentials", tone: "teal",
        description: "Portal / payer login records in scope.",
        formula: "COUNT(login_credentials) in scope." },
      { key: "kpi_cred_active", title: "Active Credentials", tone: "blue",
        description: "Credentials currently in active status.",
        formula: "COUNT(login_credentials) where status = Active." },
      { key: "kpi_cred_org", title: "Organization Credentials", tone: "violet",
        description: "Credentials attached to an organization.",
        formula: "COUNT(login_credentials) where organization_id is set." },
      { key: "kpi_cred_prov", title: "Provider Credentials", tone: "green",
        description: "Credentials attached to an individual provider.",
        formula: "COUNT(login_credentials) where provider_id is set." },
    ],
    charts: [
      { id: "lc_trend", type: "line", title: "Credential Additions", ...L.trendA,
        description: "New login credentials created each month (last 6 months).",
        formula: "COUNT(login_credentials) grouped by month of created_on." },
      { id: "lc_cumulative", type: "area", title: "Cumulative Credentials", ...L.trendB,
        description: "Running total of credential records (last 12 months).",
        formula: "Credentials created before the window, plus a running sum per month." },
      { id: "lc_per_org", type: "bar", title: "Credentials per Organization", ...L.compA,
        description: "Login records by organization (top 10).",
        formula: "COUNT(login_credentials) grouped by organization, top 10." },
      { id: "lc_per_client", type: "bar_horizontal", title: "Credentials per Client", ...L.compB,
        description: "Login records by client account (top 10).",
        formula: "COUNT(login_credentials) grouped by client, top 10." },
      { id: "lc_status", type: "doughnut", title: "Credential Status", ...L.compC,
        description: "Login records by status.",
        formula: "COUNT(login_credentials) grouped by status." },
      { id: "lc_owner_split", type: "bar_grouped", title: "Credentials by Owner Type", ...L.signal,
        description: "Organization-level versus provider-level credentials for the 10 clients with the most records.",
        formula: "Per client: COUNT(login_credentials with organization_id) vs COUNT(login_credentials with provider_id)." },
    ],
  },

  /* ================================================== CREDENTIALING == */
  credentialing: {
    kpis: [
      { key: "kpi_cr_in_cred", title: "Providers in Credentialing", tone: "teal",
        description: "Providers with the credentialing workflow flag switched on.",
        formula: "COUNT(providers) where is_credentialing = true." },
      { key: "kpi_cr_apps", title: "Payer Applications", tone: "blue",
        description: "Provider-to-payer enrolment records in scope.",
        formula: "COUNT(healthcare_payors) joined to providers in scope." },
      { key: "kpi_cr_approved", title: "Approved Enrolments", tone: "violet",
        description: "Payer applications that have an approval date recorded.",
        formula: "COUNT(healthcare_payors) where approval_date is set." },
      { key: "kpi_cr_reval", title: "Revalidation Due < 90d", tone: "green",
        description: "Enrolments whose revalidation date falls in the next 90 days.",
        formula: "COUNT(healthcare_payors) where revalidation_date between today and today + 90 days." },
    ],
    charts: [
      { id: "cr_submissions", type: "line", title: "Payer Applications Submitted", ...L.trendA,
        description: "Applications submitted each month (last 12 months).",
        formula: "COUNT(healthcare_payors) grouped by month of application_submission_date." },
      { id: "cr_approvals", type: "area", title: "Payer Approvals", ...L.trendB,
        description: "Approvals granted each month (last 12 months).",
        formula: "COUNT(healthcare_payors) grouped by month of approval_date." },
      { id: "cr_progress", type: "bar", title: "Application Progress", ...L.compA,
        description: "Where payer applications sit in the pipeline.",
        formula: "COUNT(healthcare_payors) bucketed: no submission date / submitted & unapproved / resubmitted & unapproved / approved." },
      { id: "cr_tracking", type: "bar_horizontal", title: "Monitoring Items by Status", ...L.compB,
        description: "Credentialing monitoring items grouped by status (top 10).",
        formula: "COUNT(tracking) grouped by status, top 10." },
      { id: "cr_cred_flag", type: "doughnut", title: "Credentialing Coverage", ...L.compC,
        description: "Providers with the credentialing flag on versus off.",
        formula: "COUNT(providers) split by is_credentialing true / false-or-null." },
      { id: "cr_pipeline", type: "combo", title: "Applications In vs Approvals Out", ...L.signal,
        description: "New applications submitted each month against the running total of approvals.",
        formula: "Bars: submissions per month. Line: approvals before the window plus a running sum of approvals each month." },
    ],
  },

  /* ===================================================== USERS ======== */
  users: {
    kpis: [
      { key: "kpi_u_total", title: "Total Users", tone: "teal",
        description: "User accounts in scope, any status.",
        formula: "COUNT(users) in scope." },
      { key: "kpi_u_active", title: "Active Users", tone: "blue",
        description: "User accounts currently in active status.",
        formula: "COUNT(users) where status = Active." },
      { key: "kpi_u_doctor", title: "Providers", tone: "violet",
        description: "Users flagged as clinical providers.",
        formula: "COUNT(users) where user_type = 81 (Provider)." },
      { key: "kpi_u_super", title: "SuperAdmin Users", tone: "green",
        description: "Users whose role is marked as superadmin.",
        formula: "COUNT(users) joined to roles where role.is_super_admin = true." },
    ],
    charts: [
      { id: "us_trend", type: "line", title: "User Registration Trend", ...L.trendA,
        description: "New user accounts created each month (last 6 months).",
        formula: "COUNT(users) grouped by month of created_on." },
      { id: "us_cumulative", type: "area", title: "Cumulative Users", ...L.trendB,
        description: "Running total of user accounts (last 12 months).",
        formula: "Users created before the window, plus a running sum per month." },
      { id: "us_per_role", type: "bar", title: "Users per Role", ...L.compA,
        description: "Headcount by role (top 10).",
        formula: "COUNT(users) grouped by role name, top 10." },
      { id: "us_per_client", type: "bar_horizontal", title: "Users per Client", ...L.compB,
        description: "User accounts by client (top 10).",
        formula: "COUNT(users) grouped by client, top 10." },
      { id: "us_status", type: "doughnut", title: "User Status Distribution", ...L.compC,
        description: "User accounts by status.",
        formula: "COUNT(users) grouped by status." },
      { id: "us_role_status", type: "bar_grouped", title: "Active vs Inactive by Role", ...L.signal,
        description: "How many users in each role are active versus inactive (top 8 roles).",
        formula: "Per role: COUNT(users where status = Active) and COUNT(users where status <> Active)." },
    ],
  },

  /* =============================================== ROLES & PERMISSIONS = */
  roles_permissions: {
    kpis: [
      { key: "kpi_r_total", title: "Total Roles", tone: "teal",
        description: "Roles defined in scope, any status.",
        formula: "COUNT(roles) in scope." },
      { key: "kpi_r_active", title: "Active Roles", tone: "blue",
        description: "Roles currently in active status.",
        formula: "COUNT(roles) where status = Active." },
      { key: "kpi_r_perms", title: "Total Permissions", tone: "violet",
        description: "Distinct permission entries defined across every application service.",
        formula: "COUNT(permissions) (not scoped — the catalogue is global)." },
      { key: "kpi_r_sa", title: "SuperAdmin Roles", tone: "green",
        description: "Roles marked as superadmin.",
        formula: "COUNT(roles) where is_super_admin = true." },
    ],
    charts: [
      { id: "rp_trend", type: "line", title: "Role Creation Trend", ...L.trendA,
        description: "New roles defined each month (last 6 months).",
        formula: "COUNT(roles) grouped by month of created_on." },
      { id: "rp_cumulative", type: "area", title: "Cumulative Roles", ...L.trendB,
        description: "Running total of roles (last 12 months).",
        formula: "Roles created before the window, plus a running sum per month." },
      { id: "rp_perms_per_service", type: "bar", title: "Permissions per Service", ...L.compA,
        description: "How many permission entries each application service defines (top 10).",
        formula: "COUNT(permissions) grouped by application service, top 10." },
      { id: "rp_users_per_role", type: "bar_horizontal", title: "Users per Role", ...L.compB,
        description: "Headcount assigned to each role (top 10).",
        formula: "COUNT(users) grouped by role, top 10." },
      { id: "rp_sa_split", type: "doughnut", title: "SuperAdmin vs Regular Roles", ...L.compC,
        description: "Superadmin roles versus everything else.",
        formula: "COUNT(roles) split by is_super_admin true / false." },
      { id: "rp_role_scope", type: "bar_grouped", title: "Permissions vs Users by Role", ...L.signal,
        description: "How broad each role's access is against how many people hold it (top 8 roles by permission count).",
        formula: "Per role: length of permission_ids, and COUNT(users) assigned to that role." },
    ],
  },

  /* ===================================================== SUBSCRIPTION == */
  subscription: {
    kpis: [
      { key: "kpi_sub_mrr", title: "Monthly Revenue (MRR)", tone: "teal", unit: "$",
        description: "Normalised monthly subscription revenue — identical to the Subscribers page figure.",
        formula: "SUM(plan price) over active subscribers, annual plans divided by 12.",
        secondary: [
          { key: "kpi_sub_arr", label: "Yearly (ARR)", unit: "$" },
          { key: "kpi_sub_active", label: "Active subs" },
        ] },
      { key: "kpi_sub_active", title: "Active Subscriptions", tone: "blue",
        description: "Subscriptions currently in active billing.",
        formula: "COUNT(subscribers) where status = Active." },
      { key: "kpi_sub_exp30", title: "Expiring in 30 Days", tone: "violet",
        description: "Active-window subscriptions whose end date is within the next 30 days.",
        formula: "COUNT(subscribers) where end_date between today and today + 30 days." },
      { key: "kpi_sub_new", title: "New This Month", tone: "green",
        description: "Subscriptions created since the 1st of this month.",
        formula: "COUNT(subscribers) where created_on >= first day of the current month." },
    ],
    charts: [
      { id: "sb_tx_trend", type: "line", title: "Transactions per Month", ...L.trendA,
        description: "Billing transactions recorded each month (last 12 months).",
        formula: "COUNT(transactions) grouped by month of transaction_date." },
      { id: "sb_sub_cumulative", type: "area", title: "Cumulative Subscriptions", ...L.trendB,
        description: "Running total of subscriptions over time (last 12 months).",
        formula: "Subscriptions created before the window, plus a running sum per month." },
      { id: "sb_new_subs", type: "bar", title: "New Subscriptions", ...L.compA,
        description: "New subscriptions created each month (last 6 months).",
        formula: "COUNT(subscribers) grouped by month of created_on." },
      { id: "sb_per_plan", type: "bar_horizontal", title: "Subscribers per Plan", ...L.compB,
        description: "How subscribers split across plans.",
        formula: "COUNT(subscribers) grouped by plan name." },
      { id: "sb_status", type: "doughnut", title: "Subscription Status", ...L.compC,
        description: "Subscriptions by status.",
        formula: "COUNT(subscribers) grouped by status." },
      { id: "sb_revenue", type: "combo", title: "Payments Collected", ...L.signal,
        description: "Money actually received from PAID transactions each month and the running total.",
        formula: "Bars: SUM(transaction_amount) of PAID transactions grouped by month. Line: the cumulative sum." },
    ],
  },

  /* ===================================================== AUDIT LOGS === */
  audit_logs: {
    kpis: [
      { key: "kpi_a_total", title: "Total Audit Events", tone: "teal",
        description: "Audit log entries in scope.",
        formula: "COUNT(audit_logs) in scope." },
      { key: "kpi_a_today", title: "Events Today", tone: "blue",
        description: "Audit events recorded so far today.",
        formula: "COUNT(audit_logs) where date(created_on) = today." },
      { key: "kpi_a_actors", title: "Unique Actors", tone: "violet",
        description: "Distinct users who performed an audited action.",
        formula: "COUNT(DISTINCT actor_id) across audit_logs in scope." },
      { key: "kpi_a_month", title: "Events This Month", tone: "green",
        description: "Audit events recorded since the 1st of this month.",
        formula: "COUNT(audit_logs) where created_on >= first day of the current month." },
    ],
    charts: [
      { id: "au_daily", type: "line", title: "Daily Audit Activity", ...L.trendA,
        description: "Audit entries per day over the last 30 days.",
        formula: "COUNT(audit_logs) grouped by calendar day, last 30 days." },
      { id: "au_monthly", type: "area", title: "Monthly Audit Volume", ...L.trendB,
        description: "Audit entries per month (last 12 months).",
        formula: "COUNT(audit_logs) grouped by month of created_on." },
      { id: "au_resource_type", type: "bar", title: "Events by Resource Type", ...L.compA,
        description: "Which resource types generate the most audit events (top 10).",
        formula: "COUNT(audit_logs) grouped by resource_type, top 10." },
      { id: "au_top_events", type: "bar_horizontal", title: "Top Event Actions", ...L.compB,
        description: "The most frequent audit action types (top 10).",
        formula: "COUNT(audit_logs) grouped by event, top 10." },
      { id: "au_actor_type", type: "doughnut", title: "Events by Actor Type", ...L.compC,
        description: "Audit events split by the type of actor behind them.",
        formula: "COUNT(audit_logs) grouped by actor_type, top 6." },
      { id: "au_weekday", type: "combo", title: "Activity by Weekday", ...L.signal,
        description: "Total events against the number of distinct people active, per weekday.",
        formula: "Per weekday: COUNT(audit_logs) and COUNT(DISTINCT actor_id)." },
    ],
  },

  /* ===================================================== AI · ANALYTICS = */
  ai_analytics: {
    kpis: [
      { key: "kpi_aia_feedback_ratio", title: "Satisfaction", tone: "teal", unit: "%",
        description: "Share of rated Analytics AI answers marked helpful.",
        formula: "likes ÷ (likes + dislikes), as a percentage.",
        secondary: [
          { key: "kpi_aia_likes", label: "Likes" },
          { key: "kpi_aia_dislikes", label: "Dislikes" },
        ] },
      { key: "kpi_aia_tokens_today", title: "Tokens Today", tone: "blue",
        description: "Total tokens consumed by Analytics AI today.",
        formula: "Sum of total_tokens across every Analytics AI response recorded today.",
        secondary: [
          { key: "kpi_aia_tokens_prompt_today", label: "Prompt" },
          { key: "kpi_aia_tokens_completion_today", label: "Completion" },
        ] },
      { key: "kpi_aia_success_rate", title: "Success Rate", tone: "violet", unit: "%",
        description: "Share of Analytics AI queries that completed without error.",
        formula: "successful responses ÷ total queries, as a percentage.",
        secondary: [
          { key: "kpi_aia_total", label: "Queries" },
          { key: "kpi_aia_errors", label: "Errors" },
        ] },
      { key: "kpi_aia_suggestion_adoption", title: "Suggested-Question Use", tone: "green", unit: "%",
        description: "How often a query came from a suggested follow-up rather than free typing.",
        formula: "queries where from_suggestion = true ÷ total queries, as a percentage.",
        secondary: [
          { key: "kpi_aia_suggestion_count", label: "Suggested" },
          { key: "kpi_aia_total", label: "Queries" },
        ] },
    ],
    charts: [
      { id: "aia_tokens_daily", type: "line", title: "Tokens per Day", ...L.trendA,
        description: "Analytics AI token consumption per day over the last 30 days.",
        formula: "SUM(total_tokens) grouped by calendar day, last 30 days." },
      { id: "aia_requests_cumulative", type: "area", title: "Cumulative Queries", ...L.trendB,
        description: "Running total of Analytics AI queries (last 12 months).",
        formula: "Queries before the window, plus a running sum per month." },
      { id: "aia_tokens_monthly", type: "bar", title: "Monthly Token Burn", ...L.compA,
        description: "Analytics AI tokens consumed each month (last 12 months).",
        formula: "SUM(total_tokens) grouped by month of created_on." },
      { id: "aia_errors", type: "bar_horizontal", title: "Errors by Type", ...L.compB,
        description: "Analytics AI queries that ended in an error, grouped by failure category.",
        formula: "Count of errored Analytics AI responses, grouped by error category " +
                 "(timeout / connection / empty_response / exception / …), top 10." },
      { id: "aia_outcomes", type: "doughnut", title: "Response Outcomes", ...L.compC,
        description: "Successful answers versus each failure category.",
        formula: "Every Analytics AI response, split into Success plus one slice per error category." },
      { id: "aia_latency", type: "combo", title: "Queries vs Latency", ...L.signal,
        description: "Analytics AI queries per month against the average response time.",
        formula: "Bars: Analytics AI responses per month. Line: their average response time in ms." },
    ],
  },

  /* ===================================================== AI · CODING === */
  ai_coding: {
    kpis: [
      { key: "kpi_aic_feedback_ratio", title: "Satisfaction", tone: "teal", unit: "%",
        description: "Share of rated Coding AI results marked helpful.",
        formula: "likes ÷ (likes + dislikes), as a percentage.",
        secondary: [
          { key: "kpi_aic_likes", label: "Likes" },
          { key: "kpi_aic_dislikes", label: "Dislikes" },
        ] },
      { key: "kpi_aic_tokens_today", title: "Tokens Today", tone: "blue",
        description: "Total tokens consumed by Coding AI today.",
        formula: "Sum of total_tokens across every Coding AI response recorded today.",
        secondary: [
          { key: "kpi_aic_tokens_prompt_today", label: "Prompt" },
          { key: "kpi_aic_tokens_completion_today", label: "Completion" },
        ] },
      { key: "kpi_aic_success_rate", title: "Success Rate", tone: "violet", unit: "%",
        description: "Share of Coding AI runs that completed without error.",
        formula: "successful responses ÷ total runs, as a percentage.",
        secondary: [
          { key: "kpi_aic_total", label: "Runs" },
          { key: "kpi_aic_errors", label: "Errors" },
        ] },
      { key: "kpi_aic_error_rate", title: "Error Rate", tone: "green", unit: "%",
        description: "Share of Coding AI runs that failed.",
        formula: "failed responses ÷ total runs, as a percentage.",
        secondary: [
          { key: "kpi_aic_errors", label: "Errors" },
          { key: "kpi_aic_total", label: "Runs" },
        ] },
    ],
    charts: [
      { id: "aic_tokens_daily", type: "line", title: "Tokens per Day", ...L.trendA,
        description: "Coding AI token consumption per day over the last 30 days.",
        formula: "SUM(total_tokens) grouped by calendar day, last 30 days." },
      { id: "aic_requests_cumulative", type: "area", title: "Cumulative Runs", ...L.trendB,
        description: "Running total of Coding AI runs (last 12 months).",
        formula: "Runs before the window, plus a running sum per month." },
      { id: "aic_tokens_monthly", type: "bar", title: "Monthly Token Burn", ...L.compA,
        description: "Coding AI tokens consumed each month (last 12 months).",
        formula: "SUM(total_tokens) grouped by month of created_on." },
      { id: "aic_errors", type: "bar_horizontal", title: "Errors by Type", ...L.compB,
        description: "Coding AI runs that ended in an error, grouped by failure category.",
        formula: "Count of errored Coding AI responses, grouped by error category " +
                 "(phi_blocked / bedrock_error / exception), top 10." },
      { id: "aic_outcomes", type: "doughnut", title: "Response Outcomes", ...L.compC,
        description: "Successful results versus each failure category.",
        formula: "Every Coding AI response, split into Success plus one slice per error category." },
      { id: "aic_latency", type: "combo", title: "Runs vs Latency", ...L.signal,
        description: "Coding AI runs each month against the average response time.",
        formula: "Bars: Coding AI responses per month. Line: their average response time in ms." },
    ],
  },
};

export const CALC_FALLBACK_TEXT = CALC_FALLBACK;

/** Static metadata for one tab. `ai` resolves via the `ai_${aiType}` key. */
export function tabMetrics(tabKey) {
  return TAB_METRICS[tabKey] || { kpis: [], charts: [] };
}
